import { ApiError } from "@models/error";
import { CONFLICT_ERROR_CODES, type ConfigWarning, type FieldError } from "@models/mappingConfig";

/**
 * Paths in field errors and warnings: `retrieval.ann_top_k` for a (nested)
 * field, `qa_template[key=17122].domains` for one field of one collection row.
 */
export interface ParsedPath {
  /** Dotted field path with the row selector removed, e.g. "qa_template" or "retrieval.ann_top_k". */
  field: string;
  /** Collection row key, as a string, when the path addresses a row. */
  rowKey?: string;
  /** Field inside the row, e.g. "domains". */
  rowField?: string;
}

const ROW_SELECTOR = /^(.*?)\[key=([^\]]*)\](?:\.(.+))?$/;

export const parsePath = (path: string): ParsedPath => {
  const match = ROW_SELECTOR.exec(path);
  if (!match) return { field: path };
  const [, field, rowKey, rowField] = match;
  return { field, rowKey: rowKey.replace(/^["']|["']$/g, ""), rowField: rowField || undefined };
};

/** Field errors of a 422 `invalid_changes` / `validation_failed`, if any. */
export const fieldErrorsOf = (error: unknown): FieldError[] => {
  if (!(error instanceof ApiError)) return [];
  const raw = error.detail?.field_errors;
  return Array.isArray(raw)
    ? raw.filter((e): e is FieldError => typeof e?.path === "string").map((e) => ({ path: e.path, message: String(e.message ?? "") }))
    : [];
};

/** Warnings carried by a 422 `blocking_warnings` / `unacknowledged_warnings`, if any. */
export const warningsOf = (error: unknown): ConfigWarning[] | null => {
  if (!(error instanceof ApiError)) return null;
  const raw = error.detail?.warnings;
  return Array.isArray(raw) ? (raw as ConfigWarning[]) : null;
};

/** 409 of the config API: the version or the active pointer moved under the caller. */
export const isConflict = (error: unknown): boolean =>
  error instanceof ApiError &&
  (error.statusCode === 409 || (CONFLICT_ERROR_CODES as readonly string[]).includes(error.errorCode));

export const isNotFound = (error: unknown): boolean => error instanceof ApiError && error.statusCode === 404;

export const errorCodeOf = (error: unknown): string | null => (error instanceof ApiError ? error.errorCode : null);
